import { TypeManagerDecorator } from "../../main/type.map.service";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewEncapsulation,
    ViewChildren,
    ViewChild,
    QueryList,
    ElementRef,
    Output,
    EventEmitter,
} from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import {
    IngestionFieldDefinition,
    IngestionFieldAnswer,
    IngestionSubmission,
    IngestionPoint,
    DataIngestionService,
    AnswerType,
    RegistrantsData,
    ObjectOccurrenceData,
} from "./data.ingestion.service";
import { UserprefService } from "../../services/userpref.service";
import { EventFormRuleUtil } from "../s25-event-creation-form/s25.event.form.rule.util";
import { S25RuleTreeUtil } from "../s25-rule-tree/s25.rule.tree.util";
import { Rule, RuleTreeService } from "../../services/rule.tree.service";
import { AttachmentService } from "../../services/attachment.service";
import { S25File } from "../s25-file-upload/s25.file.upload.component";
import { MasterDefinitionService } from "../../services/master.definitions/master.definition.service";
import { OrganizationService } from "../../services/organization.service";
import { DropDownItem } from "../../pojo/DropDownItem";
import { Item } from "../../pojo/Item";
import { RoleService } from "../../services/role.service";
import { ContactService } from "../../services/contact.service";
import { SearchCriteriaType } from "../../pojo/SearchCriteriaI";

@TypeManagerDecorator("s25-ng-ingestion-form")
@Component({
    selector: "s25-ng-ingestion-form",
    template: `
        @if (init) {
            <div class="c-margin-left--single c-margin-top--single">
                <h2 class="c-margin-bottom--single">{{ ingestionPoint?.ingestionPointName }}</h2>

                @if (submission?.submitted) {
                    <div class="submission-status-alert">
                        <div class="submission-status-content">
                            <div class="submission-status-icon">
                                <i class="fa fa-check-circle"></i>
                            </div>
                            <div class="submission-status-text">
                                <strong>Form Submitted</strong>
                                <p class="submission-date">
                                    Submitted on {{ formatSubmissionDate(submission.createDate) }}
                                </p>
                            </div>
                        </div>
                    </div>
                }

                @if (formValidationErrors && formValidationErrors.length > 0) {
                    <div
                        class="alert alert-danger c-margin-bottom--single"
                        role="alert"
                        aria-live="polite"
                        #formErrorContainer
                    >
                        <strong>Form Validation Errors:</strong>
                        <ul class="c-margin-bottom--none">
                            @for (error of formValidationErrors; track error) {
                                <li>{{ error }}</li>
                            }
                        </ul>
                    </div>
                }

                <div class="c-margin-bottom--single">
                    @for (fieldDef of fieldDefinitions; track fieldDef) {
                        <div
                            class="c-margin-bottom--half"
                            [hidden]="fieldDef.hidden"
                            [attr.data-field-id]="fieldDef.fieldDefinitionId"
                            [class.has-error]="fieldDef.validationError"
                            [attr.aria-invalid]="fieldDef.validationError ? 'true' : 'false'"
                        >
                            @if (fieldDef.fieldType === "SECTION") {
                                <h3 class="c-margin-top--single c-margin-bottom--single">
                                    {{ fieldDef.fieldLabel }}
                                    @if (fieldDef.infoText) {
                                        <span
                                            class="info-icon"
                                            (click)="toggleInfoText(fieldDef)"
                                            [title]="
                                                'Click to ' +
                                                (fieldDef.showInfoText ? 'hide' : 'show') +
                                                ' additional information'
                                            "
                                        >
                                            ℹ
                                        </span>
                                    }
                                </h3>
                                @if (fieldDef.infoText && fieldDef.showInfoText) {
                                    <div class="field-info-text section-info">
                                        {{ fieldDef.infoText }}
                                    </div>
                                }
                            } @else {
                                <div class="form-field">
                                    <label class="field-label">
                                        {{ fieldDef.fieldLabel }}
                                        @if (fieldDef.required) {
                                            <span class="required-asterisk">*</span>
                                        }
                                        @if (fieldDef.infoText) {
                                            <span
                                                class="info-icon"
                                                (click)="toggleInfoText(fieldDef)"
                                                [title]="
                                                    'Click to ' +
                                                    (fieldDef.showInfoText ? 'hide' : 'show') +
                                                    ' additional information'
                                                "
                                            >
                                                ℹ
                                            </span>
                                        }
                                    </label>

                                    @if (fieldDef.infoText && fieldDef.showInfoText) {
                                        <div class="field-info-text">
                                            {{ fieldDef.infoText }}
                                        </div>
                                    }

                                    @if (fieldDef.fieldType === "SELECT") {
                                        <select
                                            class="c-selectInput cn-form__control"
                                            [(ngModel)]="fieldDef.singleAnswer"
                                            (ngModelChange)="onAnswerChange(fieldDef)"
                                            (blur)="validateField(fieldDef)"
                                            [disabled]="readOnly"
                                        >
                                            <option value="">-- Select an option --</option>
                                            @for (opt of fieldDef.options; track opt) {
                                                <option [ngValue]="opt">
                                                    {{ opt.option }}
                                                </option>
                                            }
                                        </select>
                                    }

                                    @if (fieldDef.fieldType === "MULTISELECT") {
                                        <select
                                            class="c-selectInput multiSelect cn-form__control"
                                            [(ngModel)]="fieldDef.answers"
                                            multiple="multiple"
                                            (ngModelChange)="onAnswerChange(fieldDef)"
                                            (blur)="validateField(fieldDef)"
                                            [disabled]="readOnly"
                                        >
                                            @for (opt of fieldDef.options; track opt) {
                                                <option [ngValue]="opt">
                                                    {{ opt.option }}
                                                </option>
                                            }
                                        </select>
                                        @if (!readOnly && fieldDef.answers && fieldDef.answers.length > 0) {
                                            <button
                                                type="button"
                                                class="clear-all-btn"
                                                (click)="clearAllSelections(fieldDef)"
                                                title="Clear all selections"
                                            >
                                                Clear All
                                            </button>
                                        }
                                    }

                                    @if (fieldDef.fieldType === "TEXT") {
                                        @if (fieldDef.answerType === AnswerType.LARGE_STRING) {
                                            @if (readOnly) {
                                                <div
                                                    class="rich-text-display"
                                                    [innerHTML]="fieldDef.longAnswer | safeHTML"
                                                ></div>
                                            } @else {
                                                <s25-ng-rich-text-editor
                                                    [rteLabel]="''"
                                                    [(modelValue)]="fieldDef.longAnswer"
                                                    (modelValueChange)="onAnswerChange(fieldDef)"
                                                ></s25-ng-rich-text-editor>
                                            }
                                        } @else {
                                            <input
                                                class="c-input"
                                                [type]="getInputType(fieldDef.answerType)"
                                                [(ngModel)]="fieldDef.longAnswer"
                                                (ngModelChange)="onAnswerChange(fieldDef)"
                                                (blur)="validateField(fieldDef)"
                                                [min]="fieldDef.minLength"
                                                [max]="fieldDef.maxLength"
                                                [disabled]="readOnly"
                                            />
                                        }
                                    }

                                    @if (fieldDef.fieldType === "FILE") {
                                        <s25-ng-editable-file
                                            [model]="getFileModel(fieldDef)"
                                            [canEdit]="!readOnly"
                                            [excludeCustAttrFiles]="true"
                                        ></s25-ng-editable-file>
                                    }

                                    @if (isSpecialFieldType(fieldDef.fieldType)) {
                                        <s25-ng-dropdown-search-criteria
                                            [(chosen)]="fieldDef.specialFieldItem"
                                            (chosenChange)="onAnswerChange(fieldDef)"
                                            [type]="getSearchCriteriaType(fieldDef.fieldType)"
                                            [readOnly]="readOnly"
                                            class="form-control"
                                        ></s25-ng-dropdown-search-criteria>
                                    }

                                    @if (isJsonField(fieldDef)) {
                                        @if (fieldDef.fieldType === "OBJECT_OCCURRENCE") {
                                            <s25-ng-object-occurrence-field
                                                [value]="fieldDef.jsonFieldItem"
                                                [readOnly]="readOnly"
                                                (valueChange)="onOccurrenceFieldChange(fieldDef, $event)"
                                            ></s25-ng-object-occurrence-field>
                                        }

                                        @if (fieldDef.fieldType === "REGISTRANTS" && !!fieldDef.jsonFieldItem) {
                                            <div class="registrants">
                                                <select
                                                    class="cn-form__control"
                                                    [(ngModel)]="fieldDef.jsonFieldItem.type"
                                                    (ngModelChange)="
                                                        fieldDef.jsonFieldItem.items = []; onAnswerChange(fieldDef)
                                                    "
                                                    [disabled]="readOnly"
                                                >
                                                    <option [ngValue]="'everyone'">Everyone</option>
                                                    <option [ngValue]="'contacts'">Contacts</option>
                                                    <option [ngValue]="'orgRoles'">Organization Roles</option>
                                                </select>
                                                @if (fieldDef.jsonFieldItem?.type === "contacts") {
                                                    <s25-ng-multiselect-search-criteria
                                                        [type]="'contacts'"
                                                        [selectedItems]="fieldDef.jsonFieldItem.items || []"
                                                        (changed)="onAnswerChange(fieldDef)"
                                                        [showResults]="true"
                                                        [disabled]="readOnly"
                                                    />
                                                } @else if (fieldDef.jsonFieldItem?.type === "orgRoles") {
                                                    <s25-ng-multiselect-search-criteria
                                                        [type]="'organizationRoles'"
                                                        [selectedItems]="fieldDef.jsonFieldItem.items || []"
                                                        (changed)="onAnswerChange(fieldDef)"
                                                        [showResults]="true"
                                                        [disabled]="readOnly"
                                                    />
                                                }
                                            </div>
                                        }
                                    }

                                    @if (fieldDef.validationError) {
                                        <div
                                            class="validation-error"
                                            role="alert"
                                            aria-live="polite"
                                            [attr.id]="'error-' + fieldDef.fieldDefinitionId"
                                            #validationError
                                        >
                                            {{ fieldDef.validationError }}
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>

                <div class="form-actions">
                    @if (showCloseButton) {
                        <s25-ng-button
                            [type]="'danger--outline'"
                            [onClick]="closeForm"
                            [buttonClass]="'c-margin-right--single'"
                        >
                            Close
                        </s25-ng-button>
                    }

                    @if (!readOnly) {
                        <s25-ng-button
                            [type]="'outline'"
                            [onClick]="saveProgress"
                            [buttonClass]="'c-margin-right--single'"
                        >
                            Save Progress
                        </s25-ng-button>

                        <s25-ng-button [type]="'primary'" [onClick]="validateAndSubmit"> Submit Form </s25-ng-button>
                    }
                </div>
            </div>
        }
    `,
    styles: `
        .multiSelect {
            height: auto;
            min-height: 100px;
        }

        .clear-all-btn {
            margin-top: 0.5rem;
            padding: 0.25rem 0.75rem;
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 3px;
            color: #6c757d;
            font-size: 0.875rem;
            cursor: pointer;
            transition: all 0.2s ease;
        }

        .clear-all-btn:hover {
            background-color: #e9ecef;
            border-color: #adb5bd;
            color: #495057;
        }

        .clear-all-btn:active {
            background-color: #dee2e6;
            border-color: #6c757d;
        }

        .form-field {
            margin-bottom: 1rem;
        }

        .field-label {
            display: block;
            font-weight: bold;
            margin-bottom: 0.5rem;
        }

        .required-asterisk {
            color: red;
            margin-left: 2px;
        }

        .info-icon {
            margin-left: 5px;
            color: #2573a7;
            cursor: pointer;
            font-weight: bold;
            padding: 2px 6px;
            border-radius: 50%;
            background-color: rgba(37, 115, 167, 0.1);
            transition: background-color 0.2s ease;
        }

        .info-icon:hover {
            background-color: rgba(37, 115, 167, 0.2);
        }

        .field-info-text {
            font-size: 0.9em;
            color: #666;
            font-style: italic;
            margin-bottom: 0.5rem;
            padding: 0.75rem;
            background-color: #f8f9fa;
            border-left: 3px solid #2573a7;
            border-radius: 3px;
            animation: fadeIn 0.3s ease-in-out;
        }

        .field-info-text.section-info {
            margin-top: 0.5rem;
            margin-bottom: 1rem;
        }

        @keyframes fadeIn {
            from {
                opacity: 0;
                max-height: 0;
                padding-top: 0;
                padding-bottom: 0;
            }
            to {
                opacity: 1;
                max-height: 200px;
                padding-top: 0.75rem;
                padding-bottom: 0.75rem;
            }
        }

        .validation-error {
            color: red;
            font-size: 0.9em;
            margin-top: 0.25rem;
        }

        .alert {
            padding: 1rem;
            margin-bottom: 1rem;
            border-radius: 4px;
        }

        .alert-success {
            background-color: #d4edda;
            border-color: #c3e6cb;
            color: #155724;
        }

        .form-actions {
            margin-top: 2rem;
            border-top: 1px solid #ddd;
            padding-top: 1rem;
        }

        .rich-text-display {
            min-height: 100px;
            padding: 0.75rem;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            background-color: #f8f9fa;
            color: #495057;
            line-height: 1.5;
        }

        .rich-text-display:empty::before {
            content: "No content";
            color: #6c757d;
            font-style: italic;
        }

        .submission-status-alert {
            background-color: #d4edda;
            border: 1px solid #c3e6cb;
            border-radius: 8px;
            padding: 1rem;
            margin-bottom: 1.5rem;
            overflow: hidden;
            position: relative;
        }

        .submission-status-content {
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        .submission-status-icon {
            color: #28a745;
            font-size: 1.5rem;
            flex-shrink: 0;
        }

        .submission-status-text {
            flex: 1;
        }

        .submission-status-text strong {
            color: #155724;
            font-size: 1.1rem;
            display: block;
            margin-bottom: 0.25rem;
        }

        .submission-date {
            color: #155724;
            margin: 0;
            font-size: 0.9rem;
            opacity: 0.8;
        }

        .registrants {
            display: grid;
            row-gap: 0.5rem;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionFormComponent implements OnInit {
    // Static constant to offset ids to avoid conflicts with real item type IDs
    private static readonly ID_OFFSET = 10_000;

    @Input() submissionId: number;
    @Input() ingestionPointId: number;
    @Input() readOnly: boolean = false;
    @Input() showCloseButton: boolean = false;
    @Output() onClose = new EventEmitter<void>();
    @Output() onSubmit = new EventEmitter<void>();
    @ViewChildren("validationError", { read: ElementRef }) validationErrors!: QueryList<ElementRef>;
    @ViewChild("formErrorContainer", { read: ElementRef }) formErrorContainer?: ElementRef;

    // Expose AnswerType enum to template
    AnswerType = AnswerType;

    ingestionPoint: IngestionPoint;
    submission: IngestionSubmission;
    fieldDefinitions: IngestionFieldDefinition[];
    showHideRules: Rule[];
    validationRules: Rule[];
    origFieldHiddenState = new Map<number, boolean>();
    formValidationErrors: string[] = [];
    init = false;
    saving = false;
    dateTimeFormat = "MM/dd/yyyy 'at' h:mm a";
    contId: number;

    constructor(public cd: ChangeDetectorRef) {}

    formatSubmissionDate = (dateString: string | undefined): string => {
        if (!dateString) return "";
        return S25Datefilter.transform(dateString, this.dateTimeFormat) || "";
    };

    async ngOnInit() {
        this.dateTimeFormat = await UserprefService.getS25DateTimeformat();
        this.contId = await UserprefService.getContactId();
        this.refresh();
    }

    refresh = async () => {
        try {
            if (this.submissionId) {
                // Loading existing submission
                this.submission = await DataIngestionService.getSubmission(this.submissionId);
                if (!this.submission) {
                    throw new Error("Submission not found");
                }

                this.ingestionPointId = this.submission.ingestionPointId!;

                // Load the ingestion point and field definitions
                const [ingestionPoint, fieldDefinitions, answers, showHideRules, validationRules] = await Promise.all([
                    DataIngestionService.getIngestionPoint(this.ingestionPointId),
                    DataIngestionService.getFieldDefinitions(this.ingestionPointId),
                    DataIngestionService.getAnswers(this.submissionId),
                    RuleTreeService.getRules("ingestion", true, this.ingestionPointId.toString()),
                    RuleTreeService.getRules("ingestionValid", true, this.ingestionPointId.toString()),
                ]);

                this.ingestionPoint = ingestionPoint;
                this.fieldDefinitions = fieldDefinitions;
                this.showHideRules = showHideRules || [];
                this.validationRules = validationRules || [];

                await this.mapAnswersToFields(answers);
            } else if (this.ingestionPointId) {
                // Loading form for new submission
                const [ingestionPoint, fieldDefinitions, showHideRules, validationRules] = await Promise.all([
                    DataIngestionService.getIngestionPoint(this.ingestionPointId),
                    DataIngestionService.getFieldDefinitions(this.ingestionPointId),
                    RuleTreeService.getRules("ingestion", true, this.ingestionPointId.toString()),
                    RuleTreeService.getRules("ingestionValid", true, this.ingestionPointId.toString()),
                ]);

                this.ingestionPoint = ingestionPoint;
                this.fieldDefinitions = fieldDefinitions;
                this.showHideRules = showHideRules || [];
                this.validationRules = validationRules || [];

                // Create a new submission if we don't have one
                if (!this.submission) {
                    this.submission = await DataIngestionService.createSubmission(this.ingestionPointId);
                    this.submissionId = this.submission.submissionId!;
                }

                // Set default values for new form (no existing answers)
                await this.setDefaultValuesForNewForm();
            }

            // Store original hidden states
            this.origFieldHiddenState.clear();
            for (let fieldDef of this.fieldDefinitions) {
                if (fieldDef.fieldDefinitionId) {
                    this.origFieldHiddenState.set(fieldDef.fieldDefinitionId, fieldDef.hidden || false);
                }
            }

            // Initialize frontend convenience fields
            for (let fieldDef of this.fieldDefinitions) {
                if (!fieldDef.answers) {
                    fieldDef.answers = [];
                }
                if (!fieldDef.longAnswer) {
                    fieldDef.longAnswer = "";
                }
                if (!fieldDef.jsonFieldItem) {
                    fieldDef.jsonFieldItem = {} as ObjectOccurrenceData | RegistrantsData;
                }
            }

            // Load files for FILE field types in parallel
            const fileFieldPromises = this.fieldDefinitions
                .filter((fieldDef) => fieldDef.fieldType === "FILE")
                .map(async (fieldDef) => {
                    const fileItems =
                        (await AttachmentService.getFileList(
                            this.getOffsetSubmissionId(),
                            fieldDef.fieldDefinitionId,
                        )) || [];
                    fieldDef.files = fileItems.map(AttachmentService.convertFileItemToAttachmentItem);
                });

            await Promise.all(fileFieldPromises);

            // Rules already loaded above

            this.runRules();
            this.init = true;
            this.cd.detectChanges();
        } catch (error) {
            console.error("Failed to load form:", error);
            S25Util.showError(error);
        }
    };

    mapAnswersToFields = async (answers: IngestionFieldAnswer[]) => {
        const answerMap = new Map<number, IngestionFieldAnswer[]>();

        // Group answers by field definition ID
        for (let answer of answers) {
            if (answer.fieldDefinitionId && !answerMap.has(answer.fieldDefinitionId)) {
                answerMap.set(answer.fieldDefinitionId, []);
            }
            if (answer.fieldDefinitionId) {
                answerMap.get(answer.fieldDefinitionId)?.push(answer);
            }
        }

        // Map answers to field definitions
        for (let fieldDef of this.fieldDefinitions) {
            const fieldAnswers = fieldDef.fieldDefinitionId ? answerMap.get(fieldDef.fieldDefinitionId) || [] : [];

            if (fieldDef.fieldType === "SELECT") {
                if (fieldAnswers.length > 0) {
                    const answerValue = fieldAnswers[0].answerRaw || "";
                    fieldDef.singleAnswer = fieldDef.options?.find((opt) => opt.option === answerValue);
                }
            } else if (fieldDef.fieldType === "MULTISELECT") {
                fieldDef.answers = [];
                for (let answer of fieldAnswers) {
                    const answerValue = answer.answerRaw || "";
                    const option = fieldDef.options?.find((opt) => opt.option === answerValue);
                    if (option) {
                        fieldDef.answers.push(option);
                    }
                }
            } else if (fieldDef.fieldType === "TEXT") {
                if (fieldAnswers.length > 0) {
                    fieldDef.longAnswer = fieldAnswers[0].answerRaw || "";
                }
            } else if (this.isJsonField(fieldDef)) {
                if (fieldAnswers.length > 0) {
                    const jsonFieldItem = await this.createJsonFieldItem(fieldDef, fieldAnswers[0].answerJson);
                    fieldDef.jsonFieldItem = jsonFieldItem;
                }
            } else if (this.isSpecialFieldType(fieldDef.fieldType!)) {
                // Handle special field types (EVENT_TYPE, ORGANIZATION, etc.)
                if (fieldAnswers.length > 0) {
                    const answerValue = fieldAnswers[0].answerRaw || "";
                    const itemId = parseInt(answerValue, 10);
                    if (!isNaN(itemId)) {
                        // Create DropDownItem object with itemId and resolved name
                        const item = await this.createSpecialFieldItem(fieldDef.fieldType!, itemId);
                        fieldDef.specialFieldItem = item;
                    }
                }
            }
        }
    };

    onAnswerChange = (fieldDef: IngestionFieldDefinition) => {
        if (this.readOnly) {
            return;
        }
        this.runRules();
        this.runFormValidationRules(); // Form validation might be affected by field changes
        this.autoSaveField(fieldDef);
    };

    onOccurrenceFieldChange = (fieldDef: IngestionFieldDefinition, event: ObjectOccurrenceData | undefined) => {
        if (this.readOnly) {
            return;
        }
        fieldDef.jsonFieldItem = event;
        this.validateField(fieldDef);
        this.runRules();
        this.runFormValidationRules();
        this.autoSaveField(fieldDef);
    };

    runRules = () => {
        // Run show/hide rules
        let satRules = EventFormRuleUtil.getSatisfiedRules({
            rules: this.showHideRules || [],
            ingestionFields: this.fieldDefinitions,
        });

        let fieldsWithAction = new Set<number>();
        satRules.sync.forEach((r) => {
            if (!r) return;
            const rule = S25RuleTreeUtil.parseRule(r);
            for (let [action, items] of Object.entries(rule.targets)) {
                if (action === "showField") {
                    items.forEach((item) => {
                        this.fieldDefinitions.forEach((f) => {
                            if (f.fieldDefinitionId === item.itemId && !fieldsWithAction.has(f.fieldDefinitionId)) {
                                fieldsWithAction.add(f.fieldDefinitionId);
                                f.hidden = false;
                            }
                        });
                    });
                } else if (action === "hideField") {
                    items.forEach((item) => {
                        this.fieldDefinitions.forEach((f) => {
                            if (f.fieldDefinitionId === item.itemId && !fieldsWithAction.has(f.fieldDefinitionId)) {
                                fieldsWithAction.add(f.fieldDefinitionId);
                                f.hidden = true;
                            }
                        });
                    });
                }
            }
        });

        // Reset fields that don't have rule actions to their original state
        this.fieldDefinitions.forEach((f) => {
            if (f.fieldDefinitionId && !fieldsWithAction.has(f.fieldDefinitionId)) {
                f.hidden = this.origFieldHiddenState.get(f.fieldDefinitionId) || false;
            }
        });

        this.cd.detectChanges();
        this.saveProgress();
    };

    validateField = (fieldDef: IngestionFieldDefinition) => {
        fieldDef.validationError = "";

        if (fieldDef.fieldType === "SECTION" || fieldDef.hidden) {
            return;
        }

        // Required validation
        if (fieldDef.required) {
            if (fieldDef.fieldType === "SELECT" && !fieldDef.singleAnswer) {
                fieldDef.validationError = "This field is required";
                return;
            } else if (fieldDef.fieldType === "MULTISELECT" && (!fieldDef.answers || fieldDef.answers.length === 0)) {
                fieldDef.validationError = "This field is required";
                return;
            } else if (fieldDef.fieldType === "TEXT" && (!fieldDef.longAnswer || fieldDef.longAnswer.trim() === "")) {
                fieldDef.validationError = "This field is required";
                return;
            } else if (fieldDef.fieldType === "OBJECT_OCCURRENCE") {
                // Check if there's at least one occurrence
                const occurrenceData = fieldDef.jsonFieldItem as ObjectOccurrenceData | undefined;
                if (
                    !occurrenceData ||
                    !occurrenceData.occurrences ||
                    !Array.isArray(occurrenceData.occurrences) ||
                    occurrenceData.occurrences.length === 0
                ) {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            } else if (this.isSpecialFieldType(fieldDef.fieldType!)) {
                // Check if there's a selected value (integer)
                if (!fieldDef.specialFieldItem || !fieldDef.specialFieldItem.itemId) {
                    fieldDef.validationError = "This field is required";
                    return;
                }
            }
        }

        // Only validate TEXT fields - SELECT/MULTISELECT options are predefined and valid
        if (fieldDef.fieldType === "TEXT" && fieldDef.longAnswer?.trim()) {
            const error = this.validateSingleValue(fieldDef.longAnswer, fieldDef);
            if (error) {
                fieldDef.validationError = error;
                return;
            }
        }

        this.cd.detectChanges();
    };

    validateSingleValue = (value: string, fieldDef: IngestionFieldDefinition): string | null => {
        // required validation is handled by the fieldDef.required check
        if (S25Util.isUndefined(value) || value.trim() === "") {
            return null;
        }

        const trimmedValue = value.trim();

        // Min/Max validation (numeric for INTEGER/FLOAT, length for strings)
        if (fieldDef.answerType === AnswerType.INTEGER || fieldDef.answerType === AnswerType.FLOAT) {
            const numValue = parseFloat(trimmedValue);
            if (isNaN(numValue)) {
                return "Invalid number";
            }
            if (fieldDef.minLength && numValue < fieldDef.minLength) {
                return `Minimum value is ${fieldDef.minLength}`;
            }
            if (fieldDef.maxLength && numValue > fieldDef.maxLength) {
                return `Maximum value is ${fieldDef.maxLength}`;
            }
        } else {
            // String length validation
            if (fieldDef.minLength && trimmedValue.length < fieldDef.minLength) {
                return `Minimum ${fieldDef.minLength} characters required`;
            }
            if (fieldDef.maxLength && trimmedValue.length > fieldDef.maxLength) {
                return `Maximum ${fieldDef.maxLength} characters allowed`;
            }
        }

        // Regex validation
        if (fieldDef.regexValid && fieldDef.regexValid.trim() !== "") {
            try {
                const regex = new RegExp(fieldDef.regexValid);
                if (!regex.test(trimmedValue)) {
                    return `Invalid format. Expected format: ${fieldDef.regexValid}`;
                }
            } catch (e) {
                console.warn("Invalid regex pattern:", fieldDef.regexValid);
                return `Invalid format. Expected format: ${fieldDef.regexValid}`;
            }
        }

        return null; // No validation errors
    };

    runFormValidationRules = () => {
        // Clear previous form validation errors
        this.formValidationErrors = [];

        if (!this.validationRules || this.validationRules.length === 0) {
            return;
        }

        // Run validation rules for the entire form
        let satRules = EventFormRuleUtil.getSatisfiedRules({
            rules: this.validationRules || [],
            ingestionFields: this.fieldDefinitions,
        });

        // Collect form-level validation errors
        satRules.sync.forEach((r) => {
            if (!r) return;
            const rule = S25RuleTreeUtil.parseRule(r);
            for (let [action, items] of Object.entries(rule.targets)) {
                if (action === "validateIngestion") {
                    items.forEach((item) => {
                        if (item.itemValue) {
                            // Add to form-level validation errors instead of field-level
                            this.formValidationErrors.push(item.itemValue);
                        }
                    });
                }
            }
        });

        this.cd.detectChanges();
    };

    getInputType = (answerType: string | undefined): string => {
        switch (answerType) {
            case "INTEGER":
                return "number";
            case "FLOAT":
                return "number";
            case "DATETIME":
                return "datetime-local";
            default:
                return "text";
        }
    };

    scrollToFirstError = () => {
        // First check for form-level validation errors (they appear at the top)
        if (this.formValidationErrors && this.formValidationErrors.length > 0 && this.formErrorContainer) {
            this.formErrorContainer.nativeElement.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
        }

        // Then check for field-level validation errors using ViewChildren for error elements
        if (this.validationErrors && this.validationErrors.length > 0) {
            const firstError = this.validationErrors.first;
            if (firstError) {
                firstError.nativeElement.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    };

    autoSaveField = S25Util.debounce(async (fieldDef: IngestionFieldDefinition) => {
        if (this.saving || !this.submission || this.submission.submitted) {
            return;
        }
        await this.saveFieldProgress(fieldDef);
    }, 300);

    saveFieldProgress = async (fieldDef: IngestionFieldDefinition) => {
        if (this.saving || !this.submission || this.submission.submitted || fieldDef.fieldType === "SECTION") {
            return;
        }

        // Validate field client-side first
        this.validateField(fieldDef);

        // Don't save if validation failed
        if (fieldDef.validationError) {
            this.cd.detectChanges();
            return;
        }

        this.saving = true;
        this.cd.detectChanges();

        try {
            // Use consolidated logic to collect answers for this single field
            const answers = this.collectAnswersForFields([fieldDef]);

            // Use saveAnswersForField which deletes only answers for this specific field
            await DataIngestionService.saveAnswersForField(this.submissionId, fieldDef.fieldDefinitionId!, answers);
        } catch (error) {
            console.error("Failed to save field progress:", error);
            fieldDef.validationError = "Failed to save field progress: " + S25Util.errorText(error);
        } finally {
            this.saving = false;
            this.cd.detectChanges();
        }
    };

    saveProgress = async () => {
        if (this.saving || !this.submission || this.submission.submitted) {
            return;
        }

        this.saving = true;
        this.cd.detectChanges();

        try {
            const answers = this.collectAnswers();

            // Validate only the fields that have answers we're about to save
            const fieldsWithAnswers = new Set(answers.map((a) => a.fieldDefinitionId));
            for (let fieldDef of this.fieldDefinitions) {
                if (fieldsWithAnswers.has(fieldDef.fieldDefinitionId)) {
                    this.validateField(fieldDef);
                    // Don't save progress if any field with answers has validation errors
                    if (fieldDef.validationError) {
                        this.cd.detectChanges();
                        return;
                    }
                }
            }

            await DataIngestionService.saveAnswers(this.submissionId, answers);
        } catch (error) {
            console.error("Failed to save progress:", error);
            S25Util.showError(error);
        } finally {
            this.saving = false;
            this.cd.detectChanges();
        }
    };

    validateAndSubmit = async () => {
        if (this.saving || !this.submission || this.submission.submitted) {
            return;
        }

        // Clear all validation errors first
        for (let fieldDef of this.fieldDefinitions) {
            fieldDef.validationError = "";
        }

        // Validate all fields (primitive validation only)
        let hasErrors = false;
        for (let fieldDef of this.fieldDefinitions) {
            this.validateField(fieldDef);
            if (fieldDef.validationError) {
                hasErrors = true;
            }
        }

        // Run custom validation rules at form level
        this.runFormValidationRules();

        // Check for form-level validation errors
        if (this.formValidationErrors && this.formValidationErrors.length > 0) {
            hasErrors = true;
        }

        if (hasErrors) {
            this.cd.detectChanges();
            this.scrollToFirstError();
            return;
        }

        this.saving = true;
        this.cd.detectChanges();

        try {
            // Save current answers
            const answers = await this.collectAnswers();
            await DataIngestionService.saveAnswers(this.submissionId, answers);

            // Submit the form (all validation is client-side)
            await DataIngestionService.submitSubmission(this.submissionId);

            // Emit onSubmit event for parent component
            this.onSubmit.emit();
        } catch (error) {
            console.error("Failed to submit form:", error);
            S25Util.showError(error);
        } finally {
            this.saving = false;
            this.cd.detectChanges();
        }
    };

    closeForm = () => {
        this.onClose.emit();
    };

    toggleInfoText = (fieldDef: IngestionFieldDefinition) => {
        fieldDef.showInfoText = !fieldDef.showInfoText;
        this.cd.detectChanges();
    };

    clearAllSelections = (fieldDef: IngestionFieldDefinition) => {
        if (fieldDef.fieldType === "MULTISELECT") {
            fieldDef.answers = [];
            this.onAnswerChange(fieldDef);
        }
    };

    // Helper method to collect answers for a single field or all fields
    private collectAnswersForFields = (fieldDefs: IngestionFieldDefinition[]): IngestionFieldAnswer[] => {
        const answers: IngestionFieldAnswer[] = [];

        for (let fieldDef of fieldDefs) {
            if (!fieldDef?.fieldType) continue;
            if (fieldDef.fieldType === "SECTION" || fieldDef.hidden) {
                continue;
            }

            if (fieldDef.fieldType === "SELECT" && fieldDef.singleAnswer?.option) {
                answers.push(this.newAnswer(fieldDef, fieldDef.singleAnswer.option));
            } else if (fieldDef.fieldType === "MULTISELECT") {
                for (let option of fieldDef.answers || []) {
                    answers.push(this.newAnswer(fieldDef, option.option));
                }
            } else if (fieldDef.fieldType === "TEXT" && fieldDef.longAnswer?.trim()) {
                answers.push(this.newAnswer(fieldDef, fieldDef.longAnswer.trim()));
            } else if (fieldDef.fieldType === "FILE") {
                // For FILE fields, save based on whether files exist
                const answerRaw = fieldDef.files && fieldDef.files.length > 0 ? "FILE_UPLOADED" : "";
                answers.push(this.newAnswer(fieldDef, answerRaw));
            } else if (this.isJsonField(fieldDef)) {
                const field: IngestionFieldDefinition = { ...fieldDef, jsonFieldItem: undefined };
                const jsonFieldItem = { ...fieldDef.jsonFieldItem } as RegistrantsData;
                if (fieldDef.fieldType === "REGISTRANTS") {
                    let registrantsData = jsonFieldItem as RegistrantsData;
                    // remove any ancillary data tacked onto each item
                    registrantsData.items = registrantsData.items.map((item) => ({
                        itemId: item.itemId,
                    }));
                }
                answers.push(this.newAnswer(field, JSON.stringify(jsonFieldItem)));
            } else if (this.isSpecialFieldType(fieldDef.fieldType)) {
                // Handle special field types (EVENT_TYPE, ORGANIZATION, etc.)
                if (!!fieldDef.specialFieldItem?.itemId) {
                    answers.push(this.newAnswer(fieldDef, fieldDef.specialFieldItem.itemId.toString()));
                }
            }
        }

        return answers;
    };

    collectAnswers = (): IngestionFieldAnswer[] => {
        return this.collectAnswersForFields(this.fieldDefinitions);
    };

    getFileModel = (fieldDef: IngestionFieldDefinition) => {
        if (!fieldDef?.fieldDefinitionId) return undefined;
        return {
            files: fieldDef.files || [],
            itemTypeId: this.getOffsetSubmissionId(), // Use offset submissionId as itemTypeId
            itemId: fieldDef.fieldDefinitionId, // Use fieldDefinitionId as itemId
            addAction: async (file: S25File) => {
                await AttachmentService.postFile(
                    this.getOffsetSubmissionId(),
                    fieldDef.fieldDefinitionId,
                    file.fileRef,
                    file.itemName,
                );
                // Update the field's files list
                const fileItems = await AttachmentService.getFileList(
                    this.getOffsetSubmissionId(),
                    fieldDef.fieldDefinitionId,
                );
                fieldDef.files = fileItems?.map(AttachmentService.convertFileItemToAttachmentItem) || [];
                // Save field progress for auto-saving
                await this.saveFieldProgress(fieldDef);
                this.onAnswerChange(fieldDef);
                this.cd.detectChanges();
            },
            removeAction: async (file: S25File) => {
                await AttachmentService.delFile(
                    this.getOffsetSubmissionId(),
                    fieldDef.fieldDefinitionId,
                    file.itemName,
                );
                // Update the field's files list
                const fileItems = await AttachmentService.getFileList(
                    this.getOffsetSubmissionId(),
                    fieldDef.fieldDefinitionId,
                );
                fieldDef.files = fileItems?.map(AttachmentService.convertFileItemToAttachmentItem) || [];
                // Save field progress for auto-saving
                await this.saveFieldProgress(fieldDef);
                this.onAnswerChange(fieldDef);
                this.cd.detectChanges();
            },
            showLinks: true,
        };
    };

    // Helper method to get the offset ingestion point ID for file operations
    private getOffsetSubmissionId(): number {
        return IngestionFormComponent.ID_OFFSET + this.submissionId;
    }

    // Set default values for new form (when no existing answers)
    private async setDefaultValuesForNewForm(): Promise<void> {
        let hasDefaultValues = false;

        for (let fieldDef of this.fieldDefinitions) {
            if (!fieldDef.defaultValue) {
                continue; // No default value set
            }

            if (fieldDef.fieldType === "SELECT") {
                // Find matching option for default value
                fieldDef.singleAnswer = fieldDef.options?.find((opt) => opt.option === fieldDef.defaultValue);
                if (fieldDef.singleAnswer) {
                    hasDefaultValues = true;
                }
            } else if (fieldDef.fieldType === "MULTISELECT") {
                // Split default value by | and find matching options
                const defaultValues = S25Util.array.unique(
                    fieldDef.defaultValue
                        .split("|")
                        .map((val) => val.trim())
                        .filter((val) => val),
                ); // Remove empty strings

                if (defaultValues.length > 0) {
                    fieldDef.answers = [];
                    for (const defaultValue of defaultValues) {
                        const defaultOption = fieldDef.options?.find((opt) => opt.option === defaultValue);
                        if (defaultOption) {
                            fieldDef.answers.push(defaultOption);
                        }
                    }
                    if (fieldDef.answers.length > 0) {
                        hasDefaultValues = true;
                    }
                }
            } else if (fieldDef.fieldType === "TEXT") {
                // Set default value for text fields
                fieldDef.longAnswer = fieldDef.defaultValue;
                if (fieldDef.longAnswer && fieldDef.longAnswer.trim()) {
                    hasDefaultValues = true;
                }
            } else if (this.isJsonField(fieldDef)) {
                const [defaultValue] = S25Util.parseJson(fieldDef.defaultValue);
                if (defaultValue) {
                    fieldDef.jsonFieldItem = await this.createJsonFieldItem(fieldDef, fieldDef.defaultValue);
                    hasDefaultValues = true;
                }
            } else if (this.isSpecialFieldType(fieldDef.fieldType!) && fieldDef.defaultValue) {
                // For special field types (EVENT_TYPE, ORGANIZATION, etc.), default value should be an itemId
                const hasDefault = await this.setSpecialFieldDefaultValue(fieldDef);
                if (hasDefault) {
                    hasDefaultValues = true;
                }
            }
        }

        // Persist all default values at once, as if the user entered them
        if (hasDefaultValues) {
            await this.saveProgress();
        }
    }

    // Configuration for special field types
    private static readonly SPECIAL_FIELD_CONFIG = {
        EVENT_TYPE: {
            searchCriteriaType: "eventTypes",
            itemTypeId: 7,
            getNameMethod: async (itemId: number) => {
                const eventType = await MasterDefinitionService.getById("event_type", itemId);
                return eventType?.type_name || `Event Type ${itemId}`;
            },
        },
        ORGANIZATION: {
            searchCriteriaType: "organizations",
            itemTypeId: 2,
            getNameMethod: async (itemId: number) => {
                return (await OrganizationService.getOrganizationName(itemId)) || `Organization ${itemId}`;
            },
        },
        FORM: {
            searchCriteriaType: "ingestionPoints",
            itemTypeId: 0,
            getNameMethod: async (itemId: number) => {
                return (await DataIngestionService.getIngestionPointName(itemId)) || `Form ${itemId}`;
            },
        },
    } as const;

    // Helper method to create DropDownItem object for special field types
    private async createSpecialFieldItem(fieldType: string, itemId: number): Promise<DropDownItem> {
        try {
            const config = (IngestionFormComponent.SPECIAL_FIELD_CONFIG as any)[fieldType];
            if (!config) {
                return {
                    itemId: itemId,
                    itemName: `Item ${itemId}`,
                    itemTypeId: 0,
                };
            }

            const itemName = await config.getNameMethod(itemId);
            return {
                itemId: itemId,
                itemName: itemName,
                itemTypeId: config.itemTypeId,
            };
        } catch (error) {
            console.warn(`Failed to load ${fieldType} name for itemId ${itemId}:`, error);
            return {
                itemId: itemId,
                itemName: `${fieldType} ${itemId}`,
                itemTypeId: 0,
            };
        }
    }

    // Helper method to check if a field type is a special field type
    isSpecialFieldType(fieldType: string | undefined): boolean {
        return !!fieldType && fieldType in IngestionFormComponent.SPECIAL_FIELD_CONFIG;
    }

    isJsonField(fieldDef: IngestionFieldDefinition): boolean {
        return DataIngestionService.isJsonField(fieldDef);
    }

    // Helper method to get search criteria type for special field types
    getSearchCriteriaType(fieldType: string | undefined): SearchCriteriaType["type"] | undefined {
        if (!fieldType) return undefined;
        const config = (IngestionFormComponent.SPECIAL_FIELD_CONFIG as any)[fieldType];
        return config?.searchCriteriaType || undefined;
    }

    private newAnswer(fieldDef: IngestionFieldDefinition, answerRaw: string | undefined): IngestionFieldAnswer {
        return {
            fieldDefinitionId: fieldDef.fieldDefinitionId,
            fieldDefinition: { ...fieldDef, specialFieldItem: undefined } as IngestionFieldDefinition,
            contId: this.contId,
            ingestionPointId: this.ingestionPointId,
            submissionId: this.submissionId,
            answerRaw: answerRaw,
        };
    }

    // Helper method to handle special field default value setting
    private async setSpecialFieldDefaultValue(fieldDef: IngestionFieldDefinition): Promise<boolean> {
        if (!this.isSpecialFieldType(fieldDef.fieldType!) || !fieldDef.defaultValue) {
            return false;
        }

        const itemId = parseInt(fieldDef.defaultValue, 10);
        if (isNaN(itemId)) {
            return false;
        }

        const item = await this.createSpecialFieldItem(fieldDef.fieldType!, itemId);
        fieldDef.specialFieldItem = item;
        this.cd.detectChanges();
        return true;
    }

    private async createJsonFieldItem(fieldDef: IngestionFieldDefinition, json: string | undefined) {
        const [jsonFieldItem] = S25Util.parseJson<ObjectOccurrenceData | RegistrantsData>(json);
        if (!jsonFieldItem) return {} as ObjectOccurrenceData | RegistrantsData;

        if (fieldDef.fieldType === "REGISTRANTS") {
            let registrantsData = jsonFieldItem as RegistrantsData;
            if (registrantsData.type === "everyone") return registrantsData;

            const ids = new Set((registrantsData.items || []).map((item) => item.itemId));
            if (!ids.size) return registrantsData;
            let nameMap = new Map<number, string>();
            if (registrantsData.type === "orgRoles") {
                const roles = await RoleService.getRoles(Item.Ids.Organization);
                nameMap = new Map(roles.map((role) => [role.role_id, role.role_name]));
            } else if (registrantsData.type === "contacts") {
                const contacts = await ContactService.getContactsMinimal(Array.from(ids));
                nameMap = new Map((contacts || []).map((contact) => [contact.contact_id, contact.contact_name]));
            }
            for (const item of registrantsData.items) item.itemName = nameMap.get(item.itemId);
        }

        return jsonFieldItem;
    }

    protected readonly Item = Item;
}
